import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Stack,
    Text,
    useToast,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../configs/firebase";
import { claimPhoneNumberViaBackend } from "../../utils/firebaseHelpers";

const CompletePhone = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const toast = useToast();


    const uid = searchParams.get("uid") || "";
    const email = searchParams.get("email") || "";
    const initialPhone = searchParams.get("phone") || "";

    const [phoneNumber, setPhoneNumber] = useState(initialPhone);
    const [isSaving, setIsSaving] = useState(false);

    const isValidContext = useMemo(() => Boolean(uid), [uid]);

    const cardsBorderRadii ="8px";
    const handleSave = async () => {
        try {
            if (!uid) {
                throw new Error("Missing signup context. Please sign in again.");
            }

            if (!phoneNumber.trim()) {
                throw new Error("Please enter a phone number.");
            }

            setIsSaving(true);

            await claimPhoneNumberViaBackend({
                currentUid: uid,
                phoneNumber,
            });

            toast({
                title: "Phone number saved",
                description: "Your number has been updated successfully.",
                status: "success",
                duration: 4000,
            });

            navigate("/dashboard");
        } catch (error) {
            toast({
                title: "Failed to update phone number",
                description: (error as Error).message,
                status: "error",
                duration: 5000,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } finally {
            navigate("/login");
        }
    };

    return (
        <Container w={'full'} py={12}>
            <Box
                p={8}
                borderWidth="1px"
                borderRadius={cardsBorderRadii}
                boxShadow="lg"
                bg="white"
                w={'100%'}
            >
                <Stack spacing={5}>
                    <Heading size="lg">Update your phone number</Heading>

                    {!isValidContext ? (
                        <Text color="red.500">
                            Missing signup context. Please go back and sign in again.
                        </Text>
                    ) : (
                        <>
                            <Text>
                                This phone number is already linked to another account. Please
                                enter a different number to continue.
                            </Text>

                            {email ? (
                                <Text fontSize="sm" color="gray.600">
                                    Account: {email}
                                </Text>
                            ) : null}

                            <FormControl isRequired>
                                <FormLabel>New phone number</FormLabel>
                                <Input
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    placeholder="e.g. 0647367388"
                                />
                            </FormControl>

                            <Button
                                colorScheme="teal"
                                onClick={handleSave}
                                isLoading={isSaving}
                            >
                                Save and continue
                            </Button>

                            <Button variant="ghost" onClick={handleLogout}>
                                Logout
                            </Button>
                        </>
                    )}
                </Stack>
            </Box>
        </Container>
    );
};

export default CompletePhone;